import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  SimpleButton,
  LoadingButton,
  Input,
  TextArea,
  Select,
  FormDialog,
  Toast,
  NotificationsList,
  Spinner,
  DropdownMenu,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsBoxArrowUpRight } from "react-icons/bs";
import { MdAdd } from "react-icons/md";
import { usePppApi } from "../services/pppApi";
import { usePppAiContext } from "../hooks/usePppAiContext";
import { useAuthUser } from "../hooks/useAuthUser";

const STATO_CFG = {
  ricevuta:      { cls: "bg-blue-100 text-blue-700" },
  in_valutazione:{ cls: "bg-yellow-100 text-yellow-700" },
  ammessa:       { cls: "bg-green-100 text-green-700" },
  respinta:      { cls: "bg-red-100 text-red-700" },
  ritirata:      { cls: "bg-gray-100 text-gray-500" },
};

const TIPO_CFG = {
  spontanea:      { cls: "bg-purple-100 text-purple-700" },
  risposta_avviso:{ cls: "bg-indigo-100 text-indigo-700" },
};

function StatoBadge({ stato, _locate }) {
  const cfg = STATO_CFG[stato] ?? { cls: "" };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
      {_locate(`ppp_proposta_stato_${stato}`) ?? stato}
    </span>
  );
}

function TipoBadge({ tipo, _locate }) {
  const cfg = TIPO_CFG[tipo] ?? { cls: "" };
  return (
    <span className={`inline-block whitespace-nowrap px-3 py-1 rounded text-xs font-medium ${cfg.cls}`}>
      {_locate(`ppp_proposta_tipo_${tipo}`) ?? tipo}
    </span>
  );
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

export default function ProposteList() {
  const navigate = useNavigate();
  const { _locate, setOpenForm, addToast } = useStateContext();
  const { can } = useAuthUser();
  usePppAiContext('ppp.proposte');
  const api = usePppApi();

  const [proposte, setProposte]         = useState([]);
  const [procedimenti, setProcedimenti] = useState([]);
  const [operatori, setOperatori]       = useState([]);
  const [avvisi, setAvvisi]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [createErrors, setCreateErrors] = useState(null);
  const [filterProcedimento, setFilterProcedimento] = useState("");

  const [createForm, setCreateForm] = useState(null);

  const loadProposte = (procedimentoId) => {
    setLoading(true);
    const params = procedimentoId ? { procedimentoId: parseInt(procedimentoId) } : {};
    api.listProposte(null, params, {
      onSuccess: (data) => { setProposte(data ?? []); setLoading(false); },
      onError:   () => setLoading(false),
    });
  };

  useEffect(() => {
    loadProposte(filterProcedimento);
    api.listProcedimenti({ onSuccess: (data) => setProcedimenti(data ?? []) });
    api.listAvvisiPubblici({}, { onSuccess: (data) => setAvvisi(data ?? []) });
    api.listOperatoriEconomici({ onSuccess: (data) => setOperatori(data ?? []) });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (e) => {
    const val = e.target.value;
    setFilterProcedimento(val);
    loadProposte(val);
  };

  const openCreate = () => {
    setCreateForm({
      procedimentoId: "",
      operatoreEconomicoId: "",
      titolo: "",
      tipoProposta: "spontanea",
      avvisoPubblicoId: "",
      dataRicezione: new Date().toISOString().slice(0, 10),
      importoSpese: "",
      descrizione: "",
      note: "",
    });
    setOpenForm(true);
    setCreateErrors(null);
  };

  const setField = (field) => (e) =>
    setCreateForm((f) => ({ ...f, [field]: e.target.value }));

  const handleCrea = () => {
    if (!createForm?.titolo || !createForm?.procedimentoId || !createForm?.operatoreEconomicoId) return;
    setSaving(true);
    setCreateErrors(null);
    api.registraProposta({
      procedimentoId:      parseInt(createForm.procedimentoId),
      operatoreEconomicoId:parseInt(createForm.operatoreEconomicoId),
      titolo:              createForm.titolo,
      tipoProposta:        createForm.tipoProposta,
      avvisoPubblicoId:    createForm.avvisoPubblicoId ? parseInt(createForm.avvisoPubblicoId) : null,
      dataRicezione:       createForm.dataRicezione || null,
      importoSpese:        createForm.importoSpese ? parseFloat(createForm.importoSpese) : null,
      descrizione:         createForm.descrizione || null,
      note:                createForm.note || null,
    }, {
      onSuccess: (data) => {
        addToast(<Toast duration={4} content={_locate("ppp_proposta_registrata")} />);
        setSaving(false);
        setOpenForm(false);
        navigate(`/ppp/proposte/${data.id}`);
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setCreateErrors(response.errors); },
    });
  };

  const cols = [
    { name: "action",                  label: "",                                    size: "w-10 max-w-[40px]", orderable: 0 },
    { name: "numeroProtocollo",        label: _locate("ppp_proposta_protocollo"),    size: "w-36" },
    { name: "titolo",                  label: _locate("ppp_proposta_titolo") },
    { name: "tipoProposta",            label: _locate("ppp_proposta_tipo"),          size: "w-44" },
    { name: "operatoreRagioneSociale", label: _locate("ppp_proposta_operatore") },
    { name: "procedimentoTitolo",      label: _locate("ppp_proposta_procedimento") },
    { name: "dataRicezione",           label: _locate("ppp_proposta_data_ricezione"), size: "w-32" },
    { name: "stato",                   label: _locate("ppp_proposta_stato"),         size: "w-32" },
  ];

  const cellsStyle = {
    action: (row) => (
      <DropdownMenu
        menu_items={[
          <button
            key="apri"
            onClick={() => navigate(`/ppp/proposte/${row.id}`)}
            className="w-full flex items-center gap-4 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
          >
            <BsBoxArrowUpRight />
            <span>{_locate("ppp_apri")}</span>
          </button>,
        ]}
      />
    ),
    numeroProtocollo: (row) => (
      <div className="cursor-pointer" onClick={() => navigate(`/ppp/proposte/${row.id}`)}>
        <span className="font-mono text-xs text-gray-700 dark:text-gray-300 hover:text-gray-400 dark:hover:text-[#a9a68e]">
          {row.numeroProtocollo ?? "—"}
        </span>
      </div>
    ),
    titolo: (row) => (
      <div className="cursor-pointer" onClick={() => navigate(`/ppp/proposte/${row.id}`)}>
        <span className="text-xs font-bold text-[#555454] dark:text-dark-text-normal hover:text-gray-400">{row.titolo}</span>
      </div>
    ),
    tipoProposta:            (row) => <TipoBadge tipo={row.tipoProposta} _locate={_locate} />,
    operatoreRagioneSociale: (row) => <span className="text-xs">{row.operatoreRagioneSociale || "—"}</span>,
    procedimentoTitolo:      (row) => <span className="text-xs">{row.procedimentoTitolo ?? "—"}</span>,
    dataRicezione:           (row) => <span className="text-xs">{formatDate(row.dataRicezione)}</span>,
    stato:                   (row) => <StatoBadge stato={row.stato} _locate={_locate} />,
  };

  if (loading) return <Spinner />;

  const avvisiFiltrati = createForm?.procedimentoId
    ? avvisi.filter((a) => String(a.procedimentoId) === String(createForm.procedimentoId))
    : avvisi;

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {_locate("ppp_proposte_titolo_lista")}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {_locate("ppp_proposte_sottotitolo")}
          </p>
        </div>
        {can('ppp.proposte.create') && (
          <SimpleButton customFunc={openCreate} text={<span className="flex items-center gap-1"><MdAdd size={16} />{_locate("ppp_proposta_registra")}</span>} />
        )}
      </div>

      {/* Filtro per procedimento */}
      <div className="mb-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 flex items-end gap-4">
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            {_locate("ppp_proposte_filtri")}
          </p>
          <div className="flex items-end gap-4">
            <div className="min-w-[280px]">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                {_locate("ppp_proposta_procedimento")}
              </label>
              <Select
                value={filterProcedimento}
                onChange={handleFilterChange}
                options={[
                  { value: "", label: _locate("ppp_proposte_tutti_procedimenti") },
                  ...(procedimenti ?? []).map((p) => ({ value: p.id, label: p.titolo })),
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      <Table
        cols={cols}
        dataset={proposte}
        cells_style={cellsStyle}
      />
      </div>{/* fine card */}

      {createForm && (
        <FormDialog title={_locate("ppp_proposta_nuova_titolo")} size="w-[680px]">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {_locate("ppp_proposta_procedimento")} *
                </label>
                <Select
                  value={createForm.procedimentoId}
                  onChange={setField("procedimentoId")}
                  options={[
                    { value: "", label: _locate("ppp_proposta_procedimento_placeholder") },
                    ...(procedimenti ?? []).map((p) => ({ value: p.id, label: p.titolo })),
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {_locate("ppp_proposta_tipo")} *
                </label>
                <Select
                  value={createForm.tipoProposta}
                  onChange={setField("tipoProposta")}
                  options={[
                    { value: "spontanea",       label: _locate("ppp_proposta_tipo_spontanea") },
                    { value: "risposta_avviso",  label: _locate("ppp_proposta_tipo_risposta_avviso") },
                  ]}
                />
              </div>
            </div>

            {createForm.tipoProposta === "risposta_avviso" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {_locate("ppp_proposta_avviso_riferimento")}
                </label>
                <Select
                  value={createForm.avvisoPubblicoId}
                  onChange={setField("avvisoPubblicoId")}
                  options={[
                    { value: "", label: _locate("ppp_proposta_avviso_placeholder") },
                    ...(avvisiFiltrati ?? []).map((a) => ({ value: a.id, label: a.oggetto })),
                  ]}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {_locate("ppp_proposta_operatore")} *
              </label>
              <Select
                value={createForm.operatoreEconomicoId}
                onChange={setField("operatoreEconomicoId")}
                options={[
                  { value: "", label: _locate("ppp_proposta_operatore_placeholder") },
                  ...(operatori ?? []).map((o) => ({ value: o.id, label: o.ragioneSociale })),
                ]}
              />
            </div>

            <Input
              label={`${_locate("ppp_proposta_titolo")} *`}
              value={createForm.titolo}
              onChange={setField("titolo")}
              placeholder={_locate("ppp_proposta_titolo_hint")}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label={_locate("ppp_proposta_data_ricezione")}
                type="date"
                value={createForm.dataRicezione}
                onChange={setField("dataRicezione")}
              />
              <Input
                label={_locate("ppp_proposta_importo_spese")}
                type="number"
                min="0"
                step="0.01"
                value={createForm.importoSpese}
                onChange={setField("importoSpese")}
                placeholder="Es. 15000.00"
              />
            </div>

            <TextArea
              label={_locate("ppp_proposta_descrizione")}
              rows={3}
              value={createForm.descrizione}
              onChange={setField("descrizione")}
              placeholder={_locate("ppp_proposta_descrizione_hint")}
            />

            <TextArea
              label={_locate("ppp_proposta_note")}
              rows={2}
              value={createForm.note}
              onChange={setField("note")}
            />

            <NotificationsList errors={createErrors} />

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <SimpleButton customFunc={() => setOpenForm(false)} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={
                  createForm.titolo && createForm.procedimentoId && createForm.operatoreEconomicoId
                    ? handleCrea
                    : undefined
                }
                text={_locate("ppp_proposta_registra_btn")}
              />
            </div>
          </div>
        </FormDialog>
      )}
    </div>
  );
}
